<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-6">
          <h1 class="text-3xl font-bold text-gray-900">Administration</h1>
          <div class="flex space-x-4">
            <router-link 
              to="/" 
              class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Retour au site
            </router-link>
            <button 
              @click="logout"
              class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Navigation Admin -->
      <div class="mb-8">
        <nav class="flex space-x-8">
          <button 
            @click="activeTab = 'events'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'events' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Événements
          </button>
          <button 
            @click="activeTab = 'history'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'history' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Histoire
          </button>
          <button 
            @click="activeTab = 'committee'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'committee' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Comité
          </button>
          <button 
            @click="activeTab = 'videos'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'videos' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Vidéos
          </button>
          <button 
            @click="activeTab = 'news'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'news' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Actualités
          </button>
          <button 
            @click="activeTab = 'carousel'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'carousel' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Carrousel
          </button>
          <button 
            @click="activeTab = 'settings'"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === 'settings' 
                ? 'border-primary-500 text-primary-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Paramètres
          </button>
        </nav>
      </div>

      <!-- Événements Tab -->
      <div v-if="activeTab === 'events'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Gestion des Événements</h2>
          <button 
            @click="showEventModal = true; editingEvent = null"
            class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Ajouter un événement
          </button>
        </div>

        <!-- Liste des événements -->
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li v-for="event in events" :key="event.id" class="px-6 py-4">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="text-lg font-medium text-gray-900">{{ event.title }}</h3>
                  <p class="text-sm text-gray-600">{{ event.description }}</p>
                  <div class="mt-2 flex items-center text-sm text-gray-500">
                    <svg class="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    {{ formatDate(event.date) }} - {{ event.location }}
                  </div>
                </div>
                <div class="flex space-x-2">
                  <button 
                    @click="editEvent(event)"
                    class="text-primary-600 hover:text-primary-900 text-sm font-medium"
                  >
                    Modifier
                  </button>
                  <button 
                    @click="deleteEvent(event.id)"
                    class="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Histoire Tab -->
      <div v-if="activeTab === 'history'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Gestion de l'Histoire</h2>
          <button 
            @click="showHistoryModal = true; editingHistory = null"
            class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Ajouter une entrée
          </button>
        </div>

        <!-- Liste des entrées historiques -->
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li v-for="entry in historyEntries" :key="entry.id" class="px-6 py-4">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="text-lg font-medium text-gray-900">{{ entry.title }}</h3>
                  <p class="text-sm text-gray-600">{{ entry.content.substring(0, 100) }}...</p>
                  <div class="mt-2 flex items-center text-sm text-gray-500">
                    <span class="font-medium">{{ entry.year }}</span>
                  </div>
                </div>
                <div class="flex space-x-2">
                  <button 
                    @click="editHistory(entry)"
                    class="text-primary-600 hover:text-primary-900 text-sm font-medium"
                  >
                    Modifier
                  </button>
                  <button 
                    @click="deleteHistory(entry.id)"
                    class="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Comité Tab -->
      <div v-if="activeTab === 'committee'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Gestion du Comité</h2>
          <button 
            @click="showCommitteeModal = true; editingMember = null"
            class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Ajouter un membre
          </button>
        </div>

        <!-- Liste des membres du comité -->
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li v-for="member in committeeMembers" :key="member.id" class="px-6 py-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4 flex-1">
                  <!-- Member photo preview -->
                  <div class="flex-shrink-0">
                    <div 
                      v-if="canDisplayImage(member.image_url)"
                      class="w-16 h-16 rounded-full overflow-hidden"
                    >
                      <img 
                        :src="member.image_url" 
                        :alt="member.name"
                        class="w-full h-full object-cover"
                        @error="handleImageError"
                      />
                    </div>
                    <DefaultAvatar 
                      v-else
                      size="medium"
                    />
                  </div>
                  
                  <!-- Member info -->
                  <div class="flex-1">
                    <h3 class="text-lg font-medium text-gray-900">{{ member.name }}</h3>
                    <p class="text-sm text-gray-600">{{ member.role }}</p>
                    <p class="text-sm text-gray-500">{{ member.description }}</p>
                  </div>
                </div>
                <div class="flex space-x-2">
                  <button 
                    @click="editCommittee(member)"
                    class="text-primary-600 hover:text-primary-900 text-sm font-medium"
                  >
                    Modifier
                  </button>
                  <button 
                    @click="deleteCommittee(member.id)"
                    class="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Vidéos Tab -->
      <div v-if="activeTab === 'videos'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Gestion des Vidéos</h2>
          <button 
            @click="showVideoModal = true; editingVideo = null"
            class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Ajouter une vidéo
          </button>
        </div>

        <!-- Liste des vidéos -->
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li v-for="video in videos" :key="video.id" class="px-6 py-4">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="text-lg font-medium text-gray-900">{{ video.title }}</h3>
                  <p class="text-sm text-gray-600">{{ video.description }}</p>
                  <div class="mt-2 flex items-center text-sm text-gray-500">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {{ video.video_type }}
                    </span>
                  </div>
                </div>
                <div class="flex space-x-2">
                  <button 
                    @click="editVideo(video)"
                    class="text-primary-600 hover:text-primary-900 text-sm font-medium"
                  >
                    Modifier
                  </button>
                  <button 
                    @click="deleteVideo(video.id)"
                    class="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Actualités Tab -->
      <div v-if="activeTab === 'news'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Gestion des Actualités</h2>
          <div class="text-sm text-gray-500">
            Les actualités sont synchronisées avec les réseaux sociaux
          </div>
        </div>

        <!-- Social media configuration -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Configuration des Réseaux Sociaux</h3>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Facebook -->
            <div class="border rounded-lg p-4">
              <div class="flex items-center mb-3">
                <svg class="w-6 h-6 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <h4 class="font-medium text-gray-900">Facebook</h4>
              </div>
              <p class="text-sm text-gray-600 mb-3">Page Facebook officielle</p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">@emulationstavelot</span>
                <a 
                  href="https://www.facebook.com/emulationstavelot" 
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Voir la page
                </a>
              </div>
            </div>

            <!-- Instagram -->
            <div class="border rounded-lg p-4">
              <div class="flex items-center mb-3">
                <svg class="w-6 h-6 text-pink-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.281H7.83c-.49 0-.928.438-.928.928v7.83c0 .49.438.928.928.928h8.449c.49 0 .928-.438.928-.928V8.635c0-.49-.438-.928-.928-.928z"/>
                </svg>
                <h4 class="font-medium text-gray-900">Instagram</h4>
              </div>
              <p class="text-sm text-gray-600 mb-3">Compte Instagram officiel</p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">@emulation_stavelot</span>
                <a 
                  href="https://www.instagram.com/emulation_stavelot/" 
                  target="_blank"
                  class="text-pink-600 hover:text-pink-800 text-sm font-medium"
                >
                  Voir le profil
                </a>
              </div>
            </div>

            <!-- Spotify -->
            <div class="border rounded-lg p-4">
              <div class="flex items-center mb-3">
                <svg class="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                <h4 class="font-medium text-gray-900">Spotify</h4>
              </div>
              <p class="text-sm text-gray-600 mb-3">Profil Spotify officiel</p>
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-500">Royale Harmonie l'Émulation</span>
                <a 
                  href="https://open.spotify.com/intl-fr/artist/3goQVcNG0KIOQS6nEhh518" 
                  target="_blank"
                  class="text-green-600 hover:text-green-800 text-sm font-medium"
                >
                  Voir le profil
                </a>
              </div>
            </div>
          </div>

          <div class="mt-6 p-4 bg-blue-50 rounded-lg">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">Information</h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>Les actualités sont automatiquement synchronisées avec vos comptes de réseaux sociaux. Les modifications doivent être effectuées directement sur les plateformes respectives.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Carrousel Tab -->
      <div v-if="activeTab === 'carousel'" class="space-y-6">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">Gestion du Carrousel</h2>
          <button 
            @click="showCarouselModal = true"
            class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Ajouter une photo
          </button>
        </div>

        <!-- Liste des photos du carrousel -->
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-medium text-gray-900">Photos du carrousel</h3>
            <p class="text-sm text-gray-600">Gérez les photos affichées dans le carrousel de la page d'accueil</p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            <div 
              v-for="(photo, index) in carouselPhotos" 
              :key="index"
              class="relative group"
            >
              <img 
                :src="photo.url" 
                :alt="photo.name"
                class="w-full h-48 object-cover rounded-lg shadow-md"
              />
              <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                  <button 
                    @click="deleteCarouselPhoto(index)"
                    class="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors"
                    title="Supprimer"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="mt-2">
                <p class="text-sm font-medium text-gray-900 truncate">{{ photo.name }}</p>
                <p class="text-xs text-gray-500">{{ photo.size }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paramètres Tab -->
      <div v-if="activeTab === 'settings'" class="space-y-6">
        <h2 class="text-2xl font-bold text-gray-900">Paramètres</h2>
        
        <!-- Informations de contact -->
        <div class="bg-white shadow rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Informations de Contact</h3>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Adresse</label>
              <input 
                v-model="contactInfo.address"
                type="text" 
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Email</label>
              <input 
                v-model="contactInfo.email"
                type="email" 
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Téléphone</label>
              <input 
                v-model="contactInfo.phone"
                type="tel" 
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button 
              @click="saveContactInfo"
              class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal d'événement -->
    <div v-if="showEventModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ editingEvent ? 'Modifier l\'événement' : 'Nouvel événement' }}
          </h3>
          
          <form @submit.prevent="saveEvent" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Titre</label>
              <input 
                v-model="eventForm.title"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Description</label>
              <textarea 
                v-model="eventForm.description"
                rows="3"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Date et heure</label>
              <input 
                v-model="eventForm.date"
                type="datetime-local" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Lieu</label>
              <input 
                v-model="eventForm.location"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Prix</label>
              <input 
                v-model="eventForm.price"
                type="text" 
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div class="flex items-center">
              <input 
                v-model="eventForm.is_public"
                type="checkbox" 
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label class="ml-2 block text-sm text-gray-900">Événement public</label>
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button 
                type="button"
                @click="showEventModal = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button 
                type="submit"
                class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {{ editingEvent ? 'Modifier' : 'Créer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Histoire -->
    <div v-if="showHistoryModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ editingHistory ? 'Modifier l\'entrée historique' : 'Nouvelle entrée historique' }}
          </h3>
          
          <form @submit.prevent="saveHistory" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Titre</label>
              <input 
                v-model="historyForm.title"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Année</label>
              <input 
                v-model="historyForm.year"
                type="number" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Contenu</label>
              <textarea 
                v-model="historyForm.content"
                rows="4"
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
            
            <div>
              <ImageUpload
                v-model="historyForm.image_url"
                label="Image de l'entrée historique"
                folder="history"
                @uploaded="onImageUploaded"
              />
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button 
                type="button"
                @click="showHistoryModal = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button 
                type="submit"
                class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {{ editingHistory ? 'Modifier' : 'Créer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Comité -->
    <div v-if="showCommitteeModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ editingMember ? 'Modifier le membre' : 'Nouveau membre' }}
          </h3>
          
          <form @submit.prevent="saveCommittee" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nom</label>
              <input 
                v-model="committeeForm.name"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Rôle</label>
              <input 
                v-model="committeeForm.role"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Description</label>
              <textarea 
                v-model="committeeForm.description"
                rows="3"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Ordre d'affichage</label>
              <input 
                v-model.number="committeeForm.order_index"
                type="number" 
                min="0"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <p class="mt-1 text-xs text-gray-500">Plus le nombre est bas, plus le membre apparaît en premier.</p>
            </div>
            
            <div>
              <ImageUpload
                v-model="committeeForm.image_url"
                label="Photo du membre (optionnel)"
                folder="committee"
                @uploaded="onCommitteeImageUploaded"
              />
            </div>
            
            <div class="flex items-center">
              <input 
                v-model="committeeForm.is_active"
                type="checkbox" 
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label class="ml-2 block text-sm text-gray-900">Membre actif</label>
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button 
                type="button"
                @click="showCommitteeModal = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button 
                type="submit"
                class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {{ editingMember ? 'Modifier' : 'Créer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Vidéo -->
    <div v-if="showVideoModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ editingVideo ? 'Modifier la vidéo' : 'Nouvelle vidéo' }}
          </h3>
          
          <form @submit.prevent="saveVideo" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Titre</label>
              <input 
                v-model="videoForm.title"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Description</label>
              <textarea 
                v-model="videoForm.description"
                rows="3"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">URL de la vidéo</label>
              <input 
                v-model="videoForm.video_url"
                type="url" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Type de vidéo</label>
              <select 
                v-model="videoForm.video_type"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="youtube">YouTube</option>
                <option value="iframe">Iframe</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">URL de la miniature (optionnel)</label>
              <input 
                v-model="videoForm.thumbnail_url"
                type="url" 
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div class="flex items-center">
              <input 
                v-model="videoForm.is_featured"
                type="checkbox" 
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label class="ml-2 block text-sm text-gray-900">Vidéo mise en avant</label>
            </div>
            
            <div class="flex items-center">
              <input 
                v-model="videoForm.is_active"
                type="checkbox" 
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label class="ml-2 block text-sm text-gray-900">Vidéo active</label>
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button 
                type="button"
                @click="showVideoModal = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button 
                type="submit"
                class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {{ editingVideo ? 'Modifier' : 'Créer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Photo -->
    <div v-if="showPhotoModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            {{ editingPhoto ? 'Modifier la photo' : 'Nouvelle photo' }}
          </h3>
          
          <form @submit.prevent="savePhoto" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Titre</label>
              <input 
                v-model="photoForm.title"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Description</label>
              <textarea 
                v-model="photoForm.description"
                rows="3"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">URL de l'image</label>
              <input 
                v-model="photoForm.image_url"
                type="url" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Catégorie</label>
              <select 
                v-model="photoForm.category"
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="concerts">Concerts</option>
                <option value="traditions">Traditions</option>
                <option value="events">Événements</option>
                <option value="repetitions">Répétitions</option>
                <option value="anniversaire">Anniversaire</option>
              </select>
            </div>
            
            <div class="flex items-center">
              <input 
                v-model="photoForm.is_featured"
                type="checkbox" 
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label class="ml-2 block text-sm text-gray-900">Photo mise en avant</label>
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button 
                type="button"
                @click="showPhotoModal = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button 
                type="submit"
                class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {{ editingPhoto ? 'Modifier' : 'Créer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Carrousel -->
    <div v-if="showCarouselModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            Ajouter une photo au carrousel
          </h3>
          
          <form @submit.prevent="saveCarouselPhoto" class="space-y-4">
            <div>
              <ImageUpload
                v-model="carouselForm.image_url"
                label="Photo du carrousel"
                folder="carousel"
                @uploaded="onCarouselImageUploaded"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Nom de la photo</label>
              <input 
                v-model="carouselForm.name"
                type="text" 
                required
                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ex: Concert 2025"
              />
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button 
                type="button"
                @click="showCarouselModal = false"
                class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button 
                type="submit"
                class="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Ajouter
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { performanceService } from '../services/performanceService'
import { historyService } from '../services/historyService'
import { committeeService } from '../services/committeeService'
import { videoService } from '../services/videoService'
import { photoService } from '../services/photoService'
import { authService } from '../services/authService'
import ImageUpload from '../components/ImageUpload.vue'
import DefaultAvatar from '../components/DefaultAvatar.vue'
import { canDisplayImage } from '../utils/imageUtils'

export default {
  name: 'Admin',
  components: {
    ImageUpload,
    DefaultAvatar
  },
  data() {
    return {
      activeTab: 'events',
      events: [],
      historyEntries: [],
      committeeMembers: [],
      videos: [],
      photos: [],
      carouselPhotos: [],
      showEventModal: false,
      showHistoryModal: false,
      showCommitteeModal: false,
      showVideoModal: false,
      showPhotoModal: false,
      showCarouselModal: false,
      editingEvent: null,
      editingHistory: null,
      editingMember: null,
      editingVideo: null,
      editingPhoto: null,
      eventForm: {
        title: '',
        description: '',
        date: '',
        location: '',
        price: '',
        is_public: true
      },
      historyForm: {
        title: '',
        content: '',
        year: '',
        image_url: ''
      },
      committeeForm: {
        name: '',
        role: '',
        description: '',
        image_url: '',
        order_index: 0,
        is_active: true
      },
      videoForm: {
        title: '',
        description: '',
        video_url: '',
        video_type: 'youtube',
        thumbnail_url: '',
        is_featured: false,
        order_index: 0,
        is_active: true
      },
      photoForm: {
        title: '',
        description: '',
        image_url: '',
        category: 'concerts',
        is_featured: false
      },
      carouselForm: {
        name: '',
        image_url: ''
      },
      contactInfo: {
        address: 'Basse Cour 16, 4970 Stavelot',
        email: 'rhes@emulation.be',
        phone: ''
      }
    }
  },
  async mounted() {
    // Vérifier l'authentification avec le service
    if (!authService.isAuthenticated) {
      this.$router.push('/admin/login')
      return
    }
    
    // Vérifier la validité du token
    const isValid = await authService.verifyToken()
    if (!isValid) {
      authService.logout()
      this.$router.push('/admin/login')
      return
    }
    
    await this.loadAllData()
  },
  methods: {
    async loadAllData() {
      await Promise.all([
        this.loadEvents(),
        this.loadHistory(),
        this.loadCommittee(),
        this.loadVideos(),
        this.loadPhotos(),
        this.loadCarouselPhotos()
      ])
    },
    
    async loadEvents() {
      try {
        this.events = await performanceService.getPerformances()
      } catch (error) {
        console.error('Erreur lors du chargement des événements:', error)
      }
    },
    
    async loadHistory() {
      try {
        this.historyEntries = await historyService.getHistoryEntries()
      } catch (error) {
        console.error('Erreur lors du chargement de l\'histoire:', error)
      }
    },
    
    async loadCommittee() {
      try {
        this.committeeMembers = await committeeService.getCommitteeMembers()
      } catch (error) {
        console.error('Erreur lors du chargement du comité:', error)
      }
    },
    
    async loadVideos() {
      try {
        this.videos = await videoService.getVideos()
      } catch (error) {
        console.error('Erreur lors du chargement des vidéos:', error)
      }
    },
    
    async loadPhotos() {
      try {
        this.photos = await photoService.getPhotos()
      } catch (error) {
        console.error('Erreur lors du chargement des photos:', error)
      }
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    editEvent(event) {
      this.editingEvent = event
      this.eventForm = {
        title: event.title,
        description: event.description,
        date: event.date.slice(0, 16), // Format datetime-local
        location: event.location,
        price: event.price,
        is_public: event.is_public
      }
      this.showEventModal = true
    },
    
    async saveEvent() {
      try {
        const eventData = {
          ...this.eventForm,
          date: new Date(this.eventForm.date).toISOString()
        }
        
        if (this.editingEvent) {
          await performanceService.updatePerformance(this.editingEvent.id, eventData)
        } else {
          await performanceService.createPerformance(eventData)
        }
        
        await this.loadEvents()
        this.showEventModal = false
        this.resetEventForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },
    
    async deleteEvent(eventId) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
        try {
          await performanceService.deletePerformance(eventId)
          await this.loadEvents()
        } catch (error) {
          console.error('Erreur lors de la suppression:', error)
        }
      }
    },
    
    resetEventForm() {
      this.eventForm = {
        title: '',
        description: '',
        date: '',
        location: '',
        price: '',
        is_public: true
      }
      this.editingEvent = null
    },
    
    saveHomeContent() {
      // TODO: Implémenter la sauvegarde des textes
      alert('Fonctionnalité à implémenter')
    },
    
    saveContactInfo() {
      // TODO: Implémenter la sauvegarde des informations de contact
      alert('Fonctionnalité à implémenter')
    },
    
    // Méthodes pour l'Histoire
    editHistory(entry) {
      this.editingHistory = entry
      this.historyForm = {
        title: entry.title,
        content: entry.content,
        year: entry.year,
        image_url: entry.image_url || ''
      }
      this.showHistoryModal = true
    },
    
    async saveHistory() {
      try {
        if (this.editingHistory) {
          await historyService.updateHistoryEntry(this.editingHistory.id, this.historyForm)
        } else {
          await historyService.createHistoryEntry(this.historyForm)
        }
        await this.loadHistory()
        this.showHistoryModal = false
        this.resetHistoryForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },
    
    async deleteHistory(entryId) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cette entrée historique ?')) {
        try {
          await historyService.deleteHistoryEntry(entryId)
          await this.loadHistory()
        } catch (error) {
          console.error('Erreur lors de la suppression:', error)
        }
      }
    },
    
    resetHistoryForm() {
      this.historyForm = {
        title: '',
        content: '',
        year: '',
        image_url: ''
      }
      this.editingHistory = null
    },
    
    // Méthodes pour le Comité
    editCommittee(member) {
      this.editingMember = member
      this.committeeForm = {
        name: member.name,
        role: member.role,
        description: member.description || '',
        image_url: member.image_url || '',
        order_index: member.order_index || 0,
        is_active: member.is_active
      }
      this.showCommitteeModal = true
    },
    
    async saveCommittee() {
      try {
        if (this.editingMember) {
          await committeeService.updateCommitteeMember(this.editingMember.id, this.committeeForm)
        } else {
          await committeeService.createCommitteeMember(this.committeeForm)
        }
        await this.loadCommittee()
        this.showCommitteeModal = false
        this.resetCommitteeForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },
    
    async deleteCommittee(memberId) {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce membre du comité ?')) {
        try {
          await committeeService.deleteCommitteeMember(memberId)
          await this.loadCommittee()
        } catch (error) {
          console.error('Erreur lors de la suppression:', error)
        }
      }
    },
    
    resetCommitteeForm() {
      this.committeeForm = {
        name: '',
        role: '',
        description: '',
        image_url: '',
        order_index: 0,
        is_active: true
      }
      this.editingMember = null
    },
    
    // Méthodes pour les Vidéos
    editVideo(video) {
      this.editingVideo = video
      this.videoForm = {
        title: video.title,
        description: video.description || '',
        video_url: video.video_url,
        video_type: video.video_type,
        thumbnail_url: video.thumbnail_url || '',
        is_featured: video.is_featured,
        order_index: video.order_index || 0,
        is_active: video.is_active
      }
      this.showVideoModal = true
    },
    
    async saveVideo() {
      try {
        if (this.editingVideo) {
          await videoService.updateVideo(this.editingVideo.id, this.videoForm)
        } else {
          await videoService.createVideo(this.videoForm)
        }
        await this.loadVideos()
        this.showVideoModal = false
        this.resetVideoForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },
    
    async deleteVideo(videoId) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) {
        try {
          await videoService.deleteVideo(videoId)
          await this.loadVideos()
        } catch (error) {
          console.error('Erreur lors de la suppression:', error)
        }
      }
    },
    
    resetVideoForm() {
      this.videoForm = {
        title: '',
        description: '',
        video_url: '',
        video_type: 'youtube',
        thumbnail_url: '',
        is_featured: false,
        order_index: 0,
        is_active: true
      }
      this.editingVideo = null
    },
    
    // Méthodes pour les Photos
    editPhoto(photo) {
      this.editingPhoto = photo
      this.photoForm = {
        title: photo.title,
        description: photo.description || '',
        image_url: photo.image_url,
        category: photo.category,
        is_featured: photo.is_featured
      }
      this.showPhotoModal = true
    },
    
    async savePhoto() {
      try {
        if (this.editingPhoto) {
          await photoService.updatePhoto(this.editingPhoto.id, this.photoForm)
        } else {
          await photoService.createPhoto(this.photoForm)
        }
        await this.loadPhotos()
        this.showPhotoModal = false
        this.resetPhotoForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },
    
    async deletePhoto(photoId) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
        try {
          await photoService.deletePhoto(photoId)
          await this.loadPhotos()
        } catch (error) {
          console.error('Erreur lors de la suppression:', error)
        }
      }
    },
    
    resetPhotoForm() {
      this.photoForm = {
        title: '',
        description: '',
        image_url: '',
        category: 'concerts',
        is_featured: false
      }
      this.editingPhoto = null
    },

    // Méthodes pour le Carrousel
    loadCarouselPhotos() {
      // Simuler le chargement des photos du carrousel
      // Dans un vrai projet, vous feriez un appel API
      this.carouselPhotos = [
        { name: '150.jpg', url: '/src/assets/carousel/150.jpg', size: '95 KB' },
        { name: 'arlon-1.jpg', url: '/src/assets/carousel/arlon-1.jpg', size: '124 KB' },
        { name: 'Laetare-1.jpeg', url: '/src/assets/carousel/Laetare-1.jpeg', size: '164 KB' },
        { name: 'niveze-1.jpg', url: '/src/assets/carousel/niveze-1.jpg', size: '105 KB' }
      ]
    },

    async saveCarouselPhoto() {
      try {
        // Simuler la sauvegarde
        const newPhoto = {
          name: this.carouselForm.name,
          url: this.carouselForm.image_url,
          size: 'N/A'
        }
        this.carouselPhotos.push(newPhoto)
        this.showCarouselModal = false
        this.resetCarouselForm()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      }
    },

    deleteCarouselPhoto(index) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cette photo du carrousel ?')) {
        this.carouselPhotos.splice(index, 1)
      }
    },

    resetCarouselForm() {
      this.carouselForm = {
        name: '',
        image_url: ''
      }
    },

    onImageUploaded(data) {
      // Image uploadée (carousel)
    },

    onCarouselImageUploaded(data) {
      // Image carousel uploadée
    },

    onCommitteeImageUploaded(data) {
      // Image comité uploadée
    },

    canDisplayImage(url) {
      return canDisplayImage(url)
    },

    handleImageError(event) {
      // Si l'image ne se charge pas, on peut ajouter une classe pour la masquer
      event.target.style.display = 'none'
    },
    
    logout() {
      authService.logout()
      this.$router.push('/')
    }
  }
}
</script>
